/**
 * WontDoForm — the reason capture for a "won't do" OOC transition
 * (STORY-010-004). The Confirm button is disabled until a non-empty reason is
 * entered (server requires it; 422 otherwise).
 */
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@garden/ui";
import { ADMIN_NS } from "@/features/admin/api";

const MAX_REASON = 500;

export function WontDoForm({
  onSubmit,
  onCancel,
  pending,
}: {
  onSubmit: (reason: string) => void;
  onCancel: () => void;
  pending: boolean;
}): ReactElement {
  const { t } = useTranslation(ADMIN_NS);
  const [reason, setReason] = useState("");
  const trimmed = reason.trim();

  return (
    <div className="flex flex-col gap-2" data-testid="ooc-wontdo-form">
      <label
        htmlFor="ooc-wontdo-reason"
        className="text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]"
      >
        {t("ooc.wontDo.reasonLabel")}
      </label>
      <textarea
        id="ooc-wontdo-reason"
        data-testid="ooc-wontdo-reason"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        maxLength={MAX_REASON}
        rows={3}
        className="w-full rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] p-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]"
        placeholder={t("ooc.wontDo.reasonPlaceholder")}
      />
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={pending}>
          {t("common.cancel")}
        </Button>
        <Button
          variant="primary"
          size="sm"
          data-testid="ooc-wontdo-confirm"
          disabled={trimmed.length === 0 || pending}
          onClick={() => onSubmit(trimmed)}
        >
          {t("ooc.wontDo.confirm")}
        </Button>
      </div>
    </div>
  );
}

export default WontDoForm;
